/**
 * Shared content-preview primitives for encounter timeline cards.
 *
 * Used by both the full patient timeline (EncountersListPage) and the
 * compact sidebar timeline (SidebarVisitsPanel) so Forms / Notes / Files /
 * Invoices / Vitals render identically in both surfaces.
 */
import { NavIcon } from '../../../shell/shared';
import type {
  EncounterFileDetailRow,
  EncounterFormPreviewRow,
  EncounterInvoicePreviewRow,
  EncounterInvoiceItemRow,
  EncounterVitalsDetailRow,
} from '@helios/data-access';

// ─── Limits ───

export const PREVIEW_LIMIT = 2;
export const PREVIEW_LIMIT_FOCUSED = 5;

// ─── Helpers ───

export function firstLine(body: string | null): string {
  if (!body) return '';
  const line = body.split('\n')[0].trim();
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
}

export function formatInvoiceAmount(amount: number | null): string {
  if (amount == null) return '';
  return `₱${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function invoiceStatusColor(status: string | null): string {
  if (status === 'paid') return 'var(--color-success-text)';
  if (status === 'issued') return 'var(--color-info, #2563eb)';
  return 'var(--text-muted)';
}

export function isImageFile(f: EncounterFileDetailRow): boolean {
  return f.kind === 'image' || (f.mime_type ?? '').startsWith('image/');
}

export function docIconForMime(mime: string | null, filename: string): string {
  const m = (mime ?? '').toLowerCase();
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (m === 'application/pdf' || ext === 'pdf') return 'file-text';
  if (m.startsWith('text/') || ['txt', 'md', 'rtf'].includes(ext)) return 'file-text';
  if (
    ['doc', 'docx'].includes(ext) ||
    m.includes('word') ||
    m.includes('officedocument.wordprocessing')
  )
    return 'file-text';
  return 'file';
}

export function formatFormDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Primitives ───

export function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-placeholder)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: 2,
      }}
    >
      {label} · {count}
    </div>
  );
}

export function PreviewRow({
  icon,
  main,
  right,
  rightColor,
  iconColor,
}: {
  icon: string;
  main: string;
  right?: string | null;
  rightColor?: string;
  iconColor?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 'var(--font-size-xs)',
        minWidth: 0,
      }}
    >
      <span style={{ color: iconColor ?? 'var(--text-placeholder)', display: 'inline-flex' }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {main}
      </span>
      {right && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: rightColor ?? 'var(--text-muted)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

export interface NotePreviewData {
  title: string | null;
  body: string | null;
  author_name: string | null;
  alert_level: string | null;
  created_at?: string | null;
}

function alertColorFor(level: string | null | undefined): string | undefined {
  if (level === 'critical') return '#dc2626';
  if (level === 'warning') return '#d97706';
  if (level === 'info') return '#2563eb';
  return undefined;
}

/**
 * Full-content note preview. Unlike the single-line PreviewRow, this shows
 * the note title (if present) and the complete multi-line body with line
 * breaks preserved, so the timeline shows the same detail as the Notes tab.
 */
export function NotePreview({ note }: { note: NotePreviewData }) {
  const iconColor = alertColorFor(note.alert_level);
  const title = note.title?.trim();
  const body = note.body?.trim();
  const hasAnyText = Boolean(title || body);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 'var(--font-size-xs)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}
      >
        <span style={{ color: iconColor ?? 'var(--text-placeholder)', display: 'inline-flex' }}>
          <NavIcon name="edit" />
        </span>
        {title ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: 'var(--text)',
              fontWeight: 'var(--font-weight-medium)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
        ) : !body ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            (empty note)
          </span>
        ) : (
          <span style={{ flex: 1 }} />
        )}
        {note.author_name && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 10,
              color: 'var(--text-muted)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {note.author_name}
          </span>
        )}
      </div>
      {body && hasAnyText && (
        <div
          style={{
            marginLeft: 20,
            color: 'var(--text)',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            lineHeight: 1.45,
          }}
        >
          {body}
        </div>
      )}
    </div>
  );
}

export function MoreLink({ count, singular }: { count: number; singular: string }) {
  return (
    <span
      style={{
        fontSize: 10,
        color: 'var(--text-placeholder)',
        marginLeft: 20,
      }}
    >
      +{count} more {singular}
      {count > 1 ? 's' : ''}
    </span>
  );
}

export function FormStatusPill({ status }: { status: string }) {
  const isDraft = status === 'draft';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: isDraft ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
        color: isDraft ? 'var(--color-warning-text)' : 'var(--color-success-text)',
        fontSize: 10,
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {isDraft ? 'Draft' : 'Completed'}
    </span>
  );
}

export function EmbeddedFormRow({
  form,
  onOpen,
}: {
  form: EncounterFormPreviewRow;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: 'pointer',
        outline: 'none',
        minWidth: 0,
      }}
    >
      <span style={{ color: 'var(--text-muted)', display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon name="file-text" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {form.form_name ?? 'Untitled Form'}
        </div>
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            marginTop: 1,
          }}
        >
          {formatFormDate(form.completed_at ?? form.created_at)}
        </div>
      </div>
      <FormStatusPill status={form.status} />
    </div>
  );
}

export function FileThumbnail({
  file,
  onOpen,
}: {
  file: EncounterFileDetailRow;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      title={file.filename}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      style={{
        width: 52,
        height: 52,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-placeholder)',
        outline: 'none',
      }}
    >
      {file.url ? (
        <img
          src={file.url}
          alt={file.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <NavIcon name="image" />
      )}
    </div>
  );
}

export function DocumentFileRow({
  file,
  onOpen,
}: {
  file: EncounterFileDetailRow;
  onOpen: () => void;
}) {
  const icon = docIconForMime(file.mime_type, file.filename);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px 4px 2px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        cursor: 'pointer',
        minWidth: 0,
        outline: 'none',
      }}
    >
      <span style={{ color: 'var(--text-placeholder)', display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon name={icon} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {file.filename}
      </span>
      {file.tag && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: 'var(--text-muted)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {file.tag}
        </span>
      )}
    </div>
  );
}

export function VitalChip({ vital }: { vital: EncounterVitalsDetailRow }) {
  const value = vital.value_number != null ? String(vital.value_number) : (vital.value_text ?? '—');
  return (
    <span
      style={{
        padding: '1px 6px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        fontSize: 10,
        color: 'var(--text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {vital.name}:{' '}
      <span style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
        {value}
        {vital.unit ? ` ${vital.unit}` : ''}
      </span>
    </span>
  );
}

const INVOICE_ITEM_PREVIEW_LIMIT = 3;

export function InvoicePreviewRow({
  invoice,
  items = [],
  onOpen,
}: {
  invoice: EncounterInvoicePreviewRow;
  items?: EncounterInvoiceItemRow[];
  onOpen: () => void;
}) {
  const amount = formatInvoiceAmount(invoice.grand_total);
  const rightText = [invoice.status, amount].filter((x) => x).join(' · ');

  const itemNames = items
    .map((i) => {
      const name = (i.service_name ?? '').trim();
      if (!name) return null;
      const qty = i.qty ?? 1;
      return qty > 1 ? `${name} ×${qty}` : name;
    })
    .filter((n): n is string => Boolean(n));
  const visibleNames = itemNames.slice(0, INVOICE_ITEM_PREVIEW_LIMIT);
  const hiddenCount = itemNames.length - visibleNames.length;
  const itemsLine = visibleNames.join(', ') + (hiddenCount > 0 ? ` · +${hiddenCount} more` : '');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: 'pointer',
        outline: 'none',
        minWidth: 0,
      }}
    >
      <span style={{ color: 'var(--text-muted)', display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon name="credit-card" />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {invoice.invoice_no ?? 'Invoice'}
        </div>
        {itemNames.length > 0 && (
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-muted)',
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {itemsLine}
          </div>
        )}
      </div>
      {rightText && (
        <span
          style={{
            flexShrink: 0,
            fontSize: 10,
            color: invoiceStatusColor(invoice.status),
            fontWeight: 'var(--font-weight-medium)',
            whiteSpace: 'nowrap',
          }}
        >
          {rightText}
        </span>
      )}
    </div>
  );
}
